import { getActiveSession, GetAllBookingsMyMSC, RootState } from '@msc-b2c/shared-utils';
import VoyagerClubTeaser from './VoyagerClubTeaser';
import {
  AllBooking,
  GetAllBookingsMyMscResponse,
  GetContactInformationMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { useEffect, useRef, useState } from 'react';
import { ApiErrorModalProps, VoyagerClubTeaserProps } from '@msc-b2c/interfaces/types';
import { useGetCustomerContactInformation } from '../customerinformationcontainer/hooks/hooks';
import {
  getCruiseDateFromCruiseId,
  getRemainingDays,
  hasUserFilledData,
} from '../utils/myAreaVoyagerClubUtils';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { useSelector } from 'react-redux';

/** Evaluates whether the teaser should be visible based on a bookings list. */
const computeVisibility = (
  bookings: AllBooking[],
  loginMode: string,
  customerInfo: GetContactInformationMyMSCResponse | undefined
): boolean =>
  bookings.some(({ status, cruiseId, cruiseLength }: AllBooking) => {
    const cruiseDateStart = getCruiseDateFromCruiseId(cruiseId);
    if (!cruiseDateStart) return false;
    const cruiseDateEnd = cruiseDateStart.add(cruiseLength, 'day');
    const daysToEnd = getRemainingDays(cruiseDateEnd);
    const isDateValid = daysToEnd > 0 && daysToEnd < 365;
    return (
      loginMode === LoginMode.FULL &&
      status === BookingStatusEnum.BOOKED &&
      isDateValid &&
      !hasUserFilledData(customerInfo)
    );
  });

export function VoyagerClubTeaserWrapper(props: Readonly<VoyagerClubTeaserProps>) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [areUserDataOutdated, setAreUserDataOutdated] = useState<boolean>(true);
  const [error, setError] = useState<ApiErrorModalProps | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const hasFetched = useRef(false);

  const { email, goldenId, loginMode } = getActiveSession();

  // Reuse the bookings list already loaded by AllMyCruises (or any other
  // sibling that dispatches myAreaAllBookings) so we avoid a duplicate
  // /users/{email}/bookings call on pages where that data is already in Redux.
  // null means "not yet loaded" — distinguish it from [] (loaded, no bookings).
  const reduxAllBookings = useSelector((state: RootState) => state.myCruise.allBookings ?? null);

  const customerInfo = useGetCustomerContactInformation(
    areUserDataOutdated,
    setAreUserDataOutdated,
    setError,
    undefined,
    goldenId
  );

  useEffect(() => {
    if (!email || !customerInfo) return;

    // If another component on the page has already loaded allBookings into
    // Redux, reuse that data immediately — no network call needed.
    if (reduxAllBookings !== null) {
      setIsVisible(computeVisibility(reduxAllBookings, loginMode, customerInfo));
      setLoading(false);
      return;
    }

    // Guard against duplicate fetches (e.g. customerInfo reference change
    // while the fetch is already in flight).
    if (hasFetched.current) return;
    hasFetched.current = true;

    const getAllBookings = new GetAllBookingsMyMSC(false, { email });
    setLoading(true);
    getAllBookings
      .getData()
      .then((res: GetAllBookingsMyMscResponse) => {
        setIsVisible(computeVisibility(res?.bookings ?? [], loginMode, customerInfo));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerInfo, reduxAllBookings]);

  return (
    <VoyagerClubTeaser
      isVisible={isVisible}
      loading={loading}
      {...props}
    />
  );
}

VoyagerClubTeaserWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/voyagersclubteaser';

export default VoyagerClubTeaserWrapper;
